import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { AlertTriangle, Check, ChevronRight, Loader2, Send } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { FileCard } from "@/shared/components/ui/file-card";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { usePerson } from "@/shared/hooks/useAppContext";
import { useDocumentDelete } from "@/shared/hooks/useDocumentStorage";
import { supabase } from "@/shared/lib/supabase";
import { uploadDocument, DocumentLimitError } from "@/shared/utils/uploadDocument";

import uploadGraphic from "@/shared/assets/illustrations/upload-graphic.svg";
import takePictureGraphic from "@/shared/assets/illustrations/take-picture.svg";
import inviteFamilyGraphic from "@/shared/assets/illustrations/invite-family.svg";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";
import { Callout } from "@/shared/components/ui/callout";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

interface StagedFile {
  id: number;
  documentId?: number;
  file: File;
  status: "staged" | "loading" | "complete" | "error";
  progress: number;
  errorMessage: string | null;
}

interface LegalAuthorityWillUploadStepProps {
  personFirstName: string;
  onBack: () => void;
  onContinue: () => void;
  onPhoneUpload: () => void;
  onRequestUpload: () => void;
  requestedFrom?: string | null;
}

export const LegalAuthorityWillUploadStep: FC<LegalAuthorityWillUploadStepProps> = ({
  personFirstName,
  onBack,
  onContinue,
  onPhoneUpload,
  onRequestUpload,
  requestedFrom = null,
}) => {
  const personName = personFirstName || "your loved one";
  const person = usePerson();
  const estateId = person?.estate_id;
  const deleteDocument = useDocumentDelete();
  const queryClient = useQueryClient();

  const { data: existingWills } = useQuery({
    queryKey: ["willDocuments", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_documents")
        .select("id, file_name, file_size")
        .eq("estate_id", estateId!)
        .eq("document_type", "will");
      return data ?? [];
    },
    enabled: !!estateId,
  });

  const [showRequestBanner, setShowRequestBanner] = useState(!!requestedFrom);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [error, setError] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const nextFileIdRef = useRef(0);
  const hasSeeded = useRef(false);

  useEffect(() => {
    if (!existingWills || hasSeeded.current) return;
    hasSeeded.current = true;
    if (existingWills.length > 0) {
      setStagedFiles(
        existingWills.map((doc) => ({
          id: nextFileIdRef.current++,
          documentId: doc.id,
          file: new File([], doc.file_name ?? "will.pdf", {
            ...(doc.file_size ? {} : {}),
          }),
          status: "complete" as const,
          progress: 100,
          errorMessage: null,
        })),
      );
    }
  }, [existingWills]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const newFiles: StagedFile[] = Array.from(files).map((file) => ({
      id: nextFileIdRef.current++,
      file,
      status: "staged" as const,
      progress: 0,
      errorMessage: null,
    }));
    setStagedFiles((prev) => [...prev, ...newFiles]);
    setError(undefined);
  }, []);

  const handleRemoveFile = (fileId: number) => {
    const removed = stagedFiles.find((f) => f.id === fileId);
    if (removed?.documentId) {
      deleteDocument.mutate(removed.documentId, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["willDocuments", estateId] }),
      });
    }
    setStagedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleContinue = async () => {
    if (!estateId) return;

    const pendingFiles = stagedFiles.filter((f) => f.status === "staged");
    if (stagedFiles.length === 0) {
      setError("Upload the will to continue");
      return;
    }

    if (pendingFiles.length === 0) {
      onContinue();
      return;
    }

    setIsUploading(true);
    setError(undefined);

    try {
      for (const staged of pendingFiles) {
        const docId = await uploadDocument(staged.file, "will", estateId);
        setStagedFiles((prev) =>
          prev.map((f) =>
            f.id === staged.id
              ? { ...f, documentId: docId, status: "complete" as const, progress: 100 }
              : f,
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["willDocuments", estateId] });
      queryClient.invalidateQueries({ queryKey: ["willExtraction", estateId] });
      onContinue();
    } catch (err) {
      const message =
        err instanceof DocumentLimitError ? err.message : "Upload failed. Please try again.";
      setError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const hasFiles = stagedFiles.length > 0;
  const fileCount = stagedFiles.length;

  return (
    <LegalAuthorityStepLayout
      title={`Add ${personName}'s will`}
      titleTooltip={{ title: WHY_WE_ASK_TITLE, content: WHY_WE_ASK.will }}
      description="Include each page, plus any updates made after it was signed (called codicils)."
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" disabled={isUploading} onClick={handleContinue}>
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      }
    >
      {!requestedFrom && (
        <div className="flex gap-3 rounded-lg border border-alert bg-yellow-50 p-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
            <AlertTriangle className="h-4 w-4 text-warning" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Don't remove will's original staples</p>
            <p className="text-sm text-secondary">
              Photograph the will exactly as it is, staples and bindings included. Courts can
              question a will that's been taken apart, and putting that right means extra paperwork.
              Slightly curved pages are fine.
            </p>
          </div>
        </div>
      )}

      {showRequestBanner && requestedFrom && (
        <Callout
          variant="brand"
          icon={<Send className="h-4 w-4" />}
          title={
            <>
              Requested from {requestedFrom}
              <span className="ml-2 font-normal text-secondary">Sent now</span>
            </>
          }
          onDismiss={() => setShowRequestBanner(false)}
        />
      )}

      {requestedFrom && !hasFiles ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm font-semibold">Have it sooner?</p>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
          >
            <img
              src={uploadGraphic}
              alt="Browse files"
              className="h-16 w-16 shrink-0 object-contain"
            />
            <p className="flex-1 min-w-0 text-left text-sm font-medium">Browse files</p>
            <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          <button
            type="button"
            onClick={onPhoneUpload}
            className="hidden md:flex items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
          >
            <img
              src={takePictureGraphic}
              alt="Take a picture with your phone"
              className="h-16 w-16 shrink-0 object-contain"
            />
            <p className="flex-1 min-w-0 text-left text-sm font-medium">Take a picture</p>
            <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
          </button>
        </div>
      ) : hasFiles ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">
              {fileCount} {fileCount === 1 ? "document" : "documents"} added
            </p>
            {stagedFiles.every((f) => f.status === "complete") && (
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </div>
            )}
          </div>
          {stagedFiles.map((staged) => (
            <FileCard
              key={staged.id}
              fileName={staged.file.name}
              fileSize={staged.file.size}
              onRemove={() => handleRemoveFile(staged.id)}
              status={staged.status === "staged" ? "complete" : staged.status}
              progress={staged.status === "staged" ? 100 : staged.progress}
              errorMessage={staged.errorMessage ?? undefined}
            />
          ))}
          <button
            type="button"
            onClick={() => additionalInputRef.current?.click()}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary bg-white px-4 py-3 text-sm font-medium text-primary transition-colors hover:bg-subtle"
          >
            + Add another
          </button>
          <input
            ref={additionalInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <>
          <button
            type="button"
            className="flex h-[220px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-primary bg-white px-6 py-8 transition-colors hover:border-focus"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            aria-label="Choose a file or drag and drop"
          >
            <img
              src={uploadGraphic}
              alt="Upload documents"
              className="h-[90px] w-[90px] object-contain"
            />
            <p className="text-base font-medium tracking-[-0.32px]">Choose a file</p>
            <p className="text-sm text-secondary">
              Click here to pick the will from your files, or drag it in
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </button>

          <div className="flex items-center gap-6">
            <div className="h-px flex-1 bg-stone-200" aria-hidden="true" />
            <p className="shrink-0 text-2xs text-tertiary">OR</p>
            <div className="h-px flex-1 bg-stone-200" aria-hidden="true" />
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <button
              type="button"
              onClick={onPhoneUpload}
              className="hidden md:flex flex-1 items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
            >
              <img
                src={takePictureGraphic}
                alt="Take a picture with your phone"
                className="h-16 w-16 shrink-0 object-contain"
              />
              <p className="flex-1 min-w-0 text-left text-sm font-medium">
                Take a picture with your phone
              </p>
              <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
            </button>

            <button
              type="button"
              onClick={onRequestUpload}
              className="flex flex-1 items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
            >
              <img
                src={inviteFamilyGraphic}
                alt="Ask someone else to upload it"
                className="h-16 w-16 shrink-0 object-contain"
              />
              <p className="flex-1 min-w-0 text-left text-sm font-medium">
                Ask someone else to upload it
              </p>
              <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
            </button>
          </div>
        </>
      )}

      <FieldError message={error} />

      <SecurityNote text="Documents are saved to your secure vault, where you can find them anytime" />
    </LegalAuthorityStepLayout>
  );
};
